import type { GeneratedConfig } from './GeneratedConfig.js';
import type { AppConfig } from './AppConfig.js';
import type { BundleMode } from './BundleMode.js';
import type { Route } from './Route.js';
import type { ServerConfig } from './ServerConfig.js';
import type { HttpsConfig } from './HttpsConfig.js';

/**
 * The merged result of user-provided and generated configuration for Cloudpack.
 */

export type CloudpackConfig = Omit<AppConfig, 'routes' | 'server' | 'bundlerCapabilitiesRegistry'> & {
  /**
   * The path to the app's package.
   */
  appPath: string;

  /**
   * Dev server settings.
   */
  server?: Omit<ServerConfig, 'routes'> & { parsedHttps?: HttpsConfig };

  /**
   * Mode to run the app in. This is set via the CLI and defaults to `'library'`.
   */
  mode: BundleMode;

  /**
   * If true, write `bundle-info.json`, `[bundler]-input.json`, and `[bundler]-output.json`
   * files to the output folder.
   */
  logBundleInfo?: boolean;

  /**
   * Generated config settings.
   */
  generated: GeneratedConfig;

  /**
   * An immutable snapshot of the generated config as it is when start or init commands first begin
   */
  generatedSnapshot?: Readonly<GeneratedConfig>;

  /**
   * Routes which have been expanded from shorthand entries.
   */
  routes?: Route[];

  /**
   * Key value pairs of external bundler capabilities that can be enabled in the package settings.
   * - Key is the name of the capability, which will be used in `PackageSettings.bundlerCapabilities`.
   * - Value is the absolute file URL of the capability implementation (resolved by `readAppConfig`).
   */
  bundlerCapabilitiesRegistry?: Record<string, string>;

  /**
   * Configuration file paths used during config resolution.
   */
  configPaths?: {
    /** Array of application configuration file paths that were loaded. */
    appConfigPaths: string[];
    /** Path to the generated configuration file, if one was created. */
    generatedConfigPath?: string;
  };
};

/** Only include the properties that are needed for the link config to allow for a smaller zod output schema. */
export type CloudpackLinkConfig = Pick<CloudpackConfig, 'generated' | 'packageSettings'>;
